import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Warning,
  ShieldCheck,
  Storefront,
  Siren,
  ArrowRight,
  MapPinLine,
  Camera,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import WorldMap from "../components/WorldMap";
import SentimentChart from "../components/SentimentChart";
import { severityColor, timeAgo } from "../lib/format";

const KPI = ({ label, value, hint, icon: Icon, accent }) => (
  <div className="p-6 md:p-7 flex flex-col gap-3" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="eyebrow">{label}</span>
      <Icon size={16} weight="regular" style={{ color: accent }} />
    </div>
    <div className="font-heading font-light text-4xl md:text-5xl leading-none tabular-nums">
      {value}
    </div>
    <div className="text-xs text-[#6E6B65] mt-1">{hint}</div>
  </div>
);

export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const navigate = useNavigate();
  const ctx = useOutletContext() || {};

  useEffect(() => {
    (async () => {
      try {
        const [o, t, a] = await Promise.all([
          api.overview(),
          api.trends(30),
          api.alerts(6),
        ]);
        setOverview(o);
        setTrends(t);
        setAlerts(a);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="dashboard-page">
      {/* Hero */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-10 lg:mb-14"
      >
        <div className="eyebrow mb-4">Real-time cosmetovigilance · 7 regions monitored</div>
        <h1 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-4xl">
          Track every cosmetic, fragrance and perfume
          <span className="font-accent italic text-[#A66E4E]"> across the world</span>.
        </h1>
        <p className="mt-5 text-[15px] text-[#6E6B65] max-w-2xl leading-relaxed">
          <span className="font-accent italic text-[#1C1B1A]">Cosmetovigilance by GCRS</span> — a
          data-dense control room for regulators, brands, and curious consumers.
          Search any product, scan it with your camera, and instantly see authorized sellers,
          parallel importers, adverse reactions and region-specific regulatory status.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <button
            type="button"
            data-testid="hero-search-cta"
            onClick={() => navigate("/search")}
            className="bg-[#1C1B1A] text-white px-6 py-3 text-sm font-semibold tracking-wide uppercase hover:bg-[#A66E4E] transition-colors flex items-center gap-2"
          >
            Search products <ArrowRight size={14} weight="bold" />
          </button>
          <button
            type="button"
            data-testid="hero-scan-cta"
            onClick={() => ctx.openScan?.()}
            className="bg-transparent border border-[#1C1B1A] px-6 py-3 text-sm font-semibold tracking-wide uppercase hover:bg-[#1C1B1A] hover:text-white transition-colors flex items-center gap-2"
          >
            <Camera size={14} weight="bold" /> Scan a product
          </button>
          <Link
            to="/alerts"
            data-testid="hero-alerts-cta"
            className="bg-transparent border border-[#E5E2DC] px-6 py-3 text-sm font-semibold tracking-wide uppercase hover:bg-[#EFECE6] transition-colors"
          >
            Review Risk Alerts
          </Link>
        </div>
      </motion.section>

      {/* KPI row — grid borders pattern */}
      <section
        data-testid="kpi-grid"
        className="grid grid-cols-2 md:grid-cols-4 grid-borders border border-[#E5E2DC]"
      >
        <KPI
          label="Products Tracked"
          value={overview?.total_products ?? "—"}
          hint="Across 4 categories"
          icon={ShieldCheck}
          accent="#40593B"
        />
        <KPI
          label="Sellers Mapped"
          value={overview?.total_sellers ?? "—"}
          hint="Online · retail · distributor · importer"
          icon={Storefront}
          accent="#A66E4E"
        />
        <KPI
          label="Signals 30d"
          value={overview?.total_complaints ?? "—"}
          hint="Social, reviews & news"
          icon={Warning}
          accent="#F29F05"
        />
        <KPI
          label="High-Severity"
          value={overview?.high_severity ?? "—"}
          hint="Requires regulator attention"
          icon={Siren}
          accent="#D93636"
        />
      </section>

      {/* Map + Trends */}
      <section className="mt-10 grid grid-cols-1 lg:grid-cols-12 grid-borders border border-[#E5E2DC]">
        <div className="lg:col-span-8 p-6 md:p-8">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="eyebrow">Regional Risk Atlas</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-medium mt-1">
                Where the signals concentrate
              </h2>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-xs text-[#6E6B65]">
              <span className="inline-block w-2 h-2 rounded-full bg-[#40593B]" /> Monitored
              <span className="inline-block w-2 h-2 rounded-full bg-[#D93636] ml-3" /> Active risk
            </div>
          </div>
          <WorldMap regionData={overview?.by_region || []} />
        </div>

        <div className="lg:col-span-4 p-6 md:p-8">
          <div className="eyebrow">Sentiment, last 30 days</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-medium mt-1 mb-6">
            Sentiment trend
          </h2>
          <SentimentChart data={trends} />
          <div className="mt-6 grid grid-cols-3 gap-px bg-[#E5E2DC] border border-[#E5E2DC]">
            <div className="bg-white p-3">
              <div className="eyebrow">Negative</div>
              <div className="font-heading text-xl tabular-nums text-[#D93636]">
                {overview?.sentiment?.negative ?? 0}
              </div>
            </div>
            <div className="bg-white p-3">
              <div className="eyebrow">Neutral</div>
              <div className="font-heading text-xl tabular-nums text-[#6E6B65]">
                {overview?.sentiment?.neutral ?? 0}
              </div>
            </div>
            <div className="bg-white p-3">
              <div className="eyebrow">Positive</div>
              <div className="font-heading text-xl tabular-nums text-[#40593B]">
                {overview?.sentiment?.positive ?? 0}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alerts + Categories */}
      <section className="mt-10 grid grid-cols-1 lg:grid-cols-12 grid-borders border border-[#E5E2DC]">
        <div className="lg:col-span-7 p-6 md:p-8" data-testid="alerts-panel">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="eyebrow">Risk alerts</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-medium mt-1">
                Region-specific risks
              </h2>
            </div>
            <Link
              to="/alerts"
              className="eyebrow text-[#A66E4E] hover:text-[#8F5D41] flex items-center gap-1"
            >
              view all <ArrowRight size={12} weight="bold" />
            </Link>
          </div>

          <div className="divide-y divide-[#E5E2DC]">
            {alerts.length === 0 && (
              <div className="py-6 text-sm text-[#6E6B65]">No active alerts.</div>
            )}
            {alerts.map((a) => {
              const sev = severityColor(a.severity);
              return (
                <Link
                  key={a.id}
                  to={`/product/${a.product_id}`}
                  data-testid="alert-row"
                  className="flex items-start gap-4 py-4 hover:bg-[#EFECE6]/40 transition-colors px-2 -mx-2"
                >
                  <div
                    className="w-1 self-stretch"
                    style={{ background: sev.fg }}
                    aria-hidden
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span
                        className="text-[10px] uppercase tracking-[0.18em] font-semibold px-1.5 py-0.5"
                        style={{ background: sev.bg, color: sev.fg }}
                      >
                        {a.severity}
                      </span>
                      <span className="eyebrow text-[9px] flex items-center gap-1">
                        <MapPinLine size={11} /> {a.region}
                      </span>
                      <span className="text-[10px] text-[#6E6B65] font-mono ml-auto">
                        {timeAgo(a.created_at)}
                      </span>
                    </div>
                    <div className="font-heading text-lg leading-tight">{a.title}</div>
                    <div className="text-[13px] text-[#6E6B65] mt-1 leading-snug">
                      {a.brand} — <span className="italic">{a.product_name}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-5 p-6 md:p-8">
          <div className="eyebrow">Complaint taxonomy</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-medium mt-1 mb-6">
            What consumers flag
          </h2>
          <div className="space-y-3">
            {(overview?.categories || []).map((c) => {
              const max = Math.max(...(overview?.categories || []).map((x) => x.value));
              const pct = (c.value / max) * 100;
              return (
                <div key={c.name} data-testid={`cat-row-${c.name}`}>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="uppercase tracking-wider font-semibold text-[#1C1B1A]">
                      {c.name.replace("_", " ")}
                    </span>
                    <span className="font-mono text-[#6E6B65]">{c.value}</span>
                  </div>
                  <div className="h-1.5 bg-[#EFECE6]">
                    <div className="h-full bg-[#A66E4E]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <section className="mt-16 pt-8 border-t border-[#E5E2DC]">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="font-accent italic text-3xl text-[#1C1B1A]">Cosmetovigilance by GCRS</div>
            <div className="eyebrow mt-2">
              Global Cosmetic Surveillance · EU · USA · UK · India · GCC · China · South Korea
            </div>
          </div>
          <div className="text-xs text-[#6E6B65] leading-relaxed max-w-md">
            Signals synthesized from retail reviews, social chatter, regulatory feeds and
            parallel-market listings. AI-assisted categorisation &amp; visual product recognition
            via Claude Sonnet 4.5.
          </div>
        </div>
      </section>
    </div>
  );
}
